/**
 * Wires `activity.model.ts` to the two reads it needs and to the reader's
 * chosen filters.
 *
 * Calls `fetchMyActivity` for the feed and `fetchCollections` only to name a
 * share, since `collection.shared` records the level and never the grantee.
 * The two reads fail differently on purpose: a refused feed is the error
 * state, a refused collection list is an empty list, because the second is a
 * label and the first is the screen.
 *
 * The state the model cannot hold is the two chip selections and the raw
 * answers. Loading is derived from both answers being outstanding, and a
 * stale user filter is normalised on the way out rather than reset by an
 * effect, so the model sees only a filter that names someone present.
 */

import { useEffect, useState } from "react";
import type { AccessEvent, ActivityEntry, CollectionSummary } from "@ondo/shared";

import { fetchAccessTrail, fetchCollections, fetchMyActivity } from "../lib/api.ts";
import {
  accessCaption,
  accessEmptyFor,
  accessNoticeFor,
  toAccessRows,
} from "./access.model.ts";
import type { AccessRow } from "./access.model.ts";
import {
  ACCESS_COPY,
  ACTIVITY_COPY,
  ALL,
  DEFAULT_ACTIVITY_TAB,
} from "./activity.constants.ts";
import type { ActivityTabId } from "./activity.constants.ts";
import {
  actionChips,
  caption,
  describeError,
  emptyFor,
  inFilter,
  noticeFor,
  resolveFilter,
  toRows,
  userChips,
} from "./activity.model.ts";
import type { AuditRow, FilterChip } from "./activity.model.ts";

/**
 * The ACCESS tab's state, kept as its own union rather than folded into the
 * one below.
 *
 * TWO UNIONS BECAUSE THE TWO TABS FAIL INDEPENDENTLY, and that independence is
 * the ticket rather than tidiness. The feed is Stream and the trail is
 * Postgres: a Stream outage must not blank the audit, and a refused trail must
 * not blank the feed. One shared error state would make either failure take the
 * whole screen down — and the half still answering is the half a studio asking
 * "who has been in here" would most want.
 */
export type AccessView =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      /** "12 lines" — counts what is SHOWN. */
      caption: string;
      order: string;
      /** What the list cannot promise, or null when it is whole. */
      notice: string | null;
      rows: AccessRow[];
      /** Null when there is something to show. Not a boolean: the copy differs. */
      empty: { lead: string; detail: string } | null;
    };

export type ActivityView =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      /** "12 events" — counts what is SHOWN, not what was loaded. */
      caption: string;
      order: string;
      /** What the list cannot promise, or null when it is whole. */
      notice: string | null;
      users: FilterChip[];
      actions: FilterChip[];
      rows: AuditRow[];
      /** Null when there is something to show. Not a boolean: the copy differs. */
      empty: { lead: string; detail: string } | null;
    };

export interface ActivityController {
  /** Which tab is open. The view switches the panel on it. */
  tab: ActivityTabId;
  chooseTab: (tab: ActivityTabId) => void;
  view: ActivityView;
  access: AccessView;
  chooseUser: (id: string) => void;
  chooseAction: (id: string) => void;
}

/**
 * The audit log.
 *
 * ONE READ, and that is the point of the follow graph rather than a coincidence.
 * `user:<ondoId>` follows every collection and work its holder may read, so the
 * personal feed already aggregates what used to need a fan-out over collections
 * — see `apps/api/src/stream/feed-graph.ts`. Collections are still fetched, but
 * only to NAME a share: `collection.shared` deliberately records the level and
 * never the grantee, so the label has to come from somewhere.
 *
 * NO `now`, and no relative ages. An audit line reading "3 wks ago" is not
 * auditable. The pleasant consequence is that the model is clock-independent, so
 * its tests need no frozen time and cannot go flaky.
 */
export function useActivityController(input: {
  /** '/collector/me/work' — where a row about a work goes. */
  workBase: string;
  /** The reader's own ONDO id, so their acts read "You". */
  viewerOndoId: string | null;
  /**
   * Whether this reader administers the firm whose workspace is open — OND-68.
   *
   * USED FOR ONE THING ONLY: which of the two empty states the access tab
   * shows. It gates NOTHING. `access_events_read` already decides what comes
   * back — a viewer's own rows, an administrator's whole firm — so a test here
   * could only ever disagree with the server about rows it has already been
   * handed. What a sentence can do and a policy cannot is avoid telling an
   * ordinary seat that the FIRM has opened nothing when the truth is only that
   * THEY have.
   */
  administers: boolean;
}): ActivityController {
  const [tab, setTab] = useState<ActivityTabId>(DEFAULT_ACTIVITY_TAB);
  const [entries, setEntries] = useState<ActivityEntry[] | null>(null);
  const [collections, setCollections] = useState<CollectionSummary[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [events, setEvents] = useState<AccessEvent[] | null>(null);
  const [trailFailure, setTrailFailure] = useState<string | null>(null);
  const [user, setUser] = useState<string>(ALL);
  const [action, setAction] = useState<string>(ALL);

  useEffect(() => {
    let active = true;

    // The feed is the screen, so a refusal here is fatal and says so. The
    // collection names are a garnish — a share simply reads without its label —
    // so that one is swallowed, which is the same split the dashboard makes.
    fetchMyActivity()
      .then((found) => {
        if (active) setEntries(found);
      })
      .catch((error: unknown) => {
        if (active) setFailure(describeError(error));
      });

    fetchCollections()
      .then((found) => {
        if (active) setCollections(found);
      })
      .catch(() => {
        if (active) setCollections([]);
      });

    // THE TRAIL IS READ ON MOUNT, NOT ON TAB CHANGE, and its failure is its
    // own. Reading it up front means switching tabs shows an answer rather than
    // a spinner, and it costs one request on a screen that loads rarely. The
    // failure goes into a state of its own because a refused trail must not
    // blank the feed, nor the other way round — see `AccessView`.
    fetchAccessTrail()
      .then((found) => {
        if (active) setEvents(found);
      })
      .catch((error: unknown) => {
        if (active) setTrailFailure(describeError(error));
      });

    return () => {
      active = false;
    };
  }, []);

  return {
    tab,
    chooseTab: setTab,
    view: resolve({ entries, collections, failure, user, action, ...input }),
    access: resolveAccess({ events, failure: trailFailure, ...input }),
    chooseUser: setUser,
    chooseAction: setAction,
  };
}

/**
 * The access tab's whole view, assembled.
 *
 * Beside the hook for the reason `resolve` is: it is the only piece that reads
 * React state, and every decision inside it is a model call.
 */
function resolveAccess(input: {
  events: AccessEvent[] | null;
  failure: string | null;
  workBase: string;
  viewerOndoId: string | null;
  administers: boolean;
}): AccessView {
  if (input.failure) return { kind: "error", message: input.failure };
  if (input.events === null) return { kind: "loading" };

  const rows = toAccessRows({
    events: input.events,
    workBase: input.workBase,
    viewerOndoId: input.viewerOndoId,
  });

  // NO FILTER CHIPS ON THIS TAB, and the absence is deliberate rather than
  // unfinished. The feed's two axes exist because a reader hunting one act in
  // fifty needs them; a trail is read to answer "who has been in here", which
  // is the unfiltered list. A chip that silently narrowed an audit log would be
  // the one control on this screen capable of hiding a line.
  return {
    kind: "ready",
    caption: accessCaption(rows.length),
    order: ACCESS_COPY.order,
    notice: accessNoticeFor(input.events.length),
    rows,
    empty: accessEmptyFor({ total: rows.length, administers: input.administers }),
  };
}

/**
 * The whole view, assembled.
 *
 * Kept beside the hook rather than in the model because it is the only piece
 * that reads React state; every decision inside it is a model call.
 */
function resolve(input: {
  entries: ActivityEntry[] | null;
  collections: CollectionSummary[] | null;
  failure: string | null;
  user: string;
  action: string;
  workBase: string;
  viewerOndoId: string | null;
}): ActivityView {
  if (input.failure) return { kind: "error", message: input.failure };

  // Loading is DERIVED from both answers being outstanding, rather than held as
  // a flag that a third setState could contradict.
  if (input.entries === null || input.collections === null) return { kind: "loading" };

  const rows = toRows({
    entries: input.entries,
    collections: input.collections,
    workBase: input.workBase,
    viewerOndoId: input.viewerOndoId,
  });

  // Normalised rather than reset by an effect: a selection can name an actor the
  // reload no longer carries, and falling back here keeps that out of state.
  const filter = resolveFilter(rows, { user: input.user, action: input.action });
  const shown = inFilter(rows, filter);

  return {
    kind: "ready",
    caption: caption(shown.length),
    order: ACTIVITY_COPY.order,
    notice: noticeFor(input.entries.length),
    users: userChips(rows, filter),
    actions: actionChips(rows, filter),
    rows: shown,
    empty: emptyFor({ total: rows.length, shown: shown.length }),
  };
}
